import {
  TicketSalesCampaignDiscountType,
  type GiftPassBundleDiscountType,
  type GiftPassBundlePreviewPrice,
} from '@bonsai/shared';

import { fieldBoxStyle } from '../../../shared/date-time-range/picker-styles';
import { cleanDiscountText } from '../../../utils/discount-amount-text';
import { SegmentedControl } from '../../dashboards/primitives/SegmentedControl';
import { EditorFieldError } from '../../shared/editor-page/EditorFieldError';
import { EditorFormSection } from '../../shared/editor-page/EditorFormSection';

import { BundleFallbackExample } from './BundleFallbackExample';

import './gift-pass-bundle-editor.css';

export interface BundleDiscountSectionProps {
  discountType: GiftPassBundleDiscountType;
  amount: string;
  /** The bundle at the fallback price, once the terms are valid and priced. */
  atFallback: GiftPassBundlePreviewPrice | null;
  error?: string;
  onChange: (patch: { discountType?: GiftPassBundleDiscountType; amount?: string }) => void;
}

const TYPES = [
  { value: TicketSalesCampaignDiscountType.PERCENT, label: 'Percentage' },
  { value: TicketSalesCampaignDiscountType.FLAT, label: 'Flat amount' },
];

const affixStyle = { color: 'var(--nb-subtle)', fontWeight: 700, flex: '0 0 auto' } as const;

export function BundleDiscountSection(p: BundleDiscountSectionProps) {
  const flat = p.discountType === TicketSalesCampaignDiscountType.FLAT;
  return (
    <EditorFormSection
      title="Discount"
      titleId="bundle-discount-title"
      hint="Taken off the whole bundle."
    >
      <div className="gpb-ed-row">
        <SegmentedControl
          ariaLabel="Discount type"
          value={p.discountType}
          options={TYPES}
          onChange={(type) =>
            p.onChange({ discountType: type, amount: cleanDiscountText(type, p.amount) })
          }
        />
        <label style={{ ...fieldBoxStyle(false, Boolean(p.error)), width: 170 }}>
          {flat && <span style={affixStyle}>$</span>}
          <input
            aria-label={flat ? 'Dollars off the bundle' : 'Percent off the bundle'}
            aria-describedby={p.error ? 'bundle-amount-error' : undefined}
            aria-invalid={Boolean(p.error)}
            inputMode={flat ? 'decimal' : 'numeric'}
            value={p.amount}
            placeholder={flat ? '20.00' : '15'}
            onChange={(e) =>
              p.onChange({ amount: cleanDiscountText(p.discountType, e.target.value) })
            }
            className="gpb-ed-bare-input"
          />
          <span style={affixStyle}>{flat ? 'off' : '% off'}</span>
        </label>
        {p.atFallback && !p.error && <BundleFallbackExample price={p.atFallback} />}
      </div>
      {p.error && <EditorFieldError id="bundle-amount-error">{p.error}</EditorFieldError>}
    </EditorFormSection>
  );
}
